import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { PageToolbar } from '@ooh/ui/components/page-toolbar';
import type { Role } from '@ooh/contracts';
import type { RoleCatalog } from './use-role-catalog';
import { useGrantStaging } from './use-grant-staging';
import { allowDepartmentScope, allowMediaTypeScope } from './role-codes';
import { PermissionGrantList } from './permission-grant-list';

interface GrantEditorProps {
  role: Role;
  isImmutable: boolean;
  catalog: RoleCatalog;
  onPermissionsSaved?: () => void;
}

/** Staged batch-save grant editor for one role. Mount it keyed by role id so a switch drops staged edits. */
export function GrantEditor({ role, isImmutable, catalog, onPermissionsSaved }: GrantEditorProps) {
  const { t } = useTranslation('admin');
  const staging = useGrantStaging(role, isImmutable, onPermissionsSaved);

  return (
    <>
      <div className="flex-1 overflow-y-auto px-8 py-5">
        <PermissionGrantList
          resourceOrder={catalog.resourceOrder}
          permissionsByResource={catalog.groupedPermissions}
          sectionLabels={catalog.sectionLabels}
          grants={staging.grantsForSelectedRole}
          disabled={isImmutable}
          allowDepartmentScope={allowDepartmentScope}
          allowMediaTypeScope={allowMediaTypeScope}
          mediaTypes={catalog.mediaTypeOptions}
          onChangeMediaTypes={staging.handleChangeMediaTypes}
          onChangeOwnership={staging.handleChangeOwnership}
          onChangeDepartment={staging.handleChangeDepartment}
          onAddScope={staging.handleAddScope}
          onRemoveScope={staging.handleRemoveScope}
        />
      </div>

      {staging.hasUnsavedChanges ? (
        <PageToolbar
          leftSlot={
            <Button variant="outline" size="sm" onClick={staging.handleDiscardGrants}>
              {t('permissionMatrix.saveBar.discard')}
            </Button>
          }
          rightSlot={
            <Button
              size="sm"
              onClick={staging.handleSaveGrants}
              disabled={staging.isSaving}
              data-testid="save-grants-btn"
            >
              {t('permissionMatrix.saveBar.save', { count: staging.changedPermissionCount })}
            </Button>
          }
        />
      ) : null}
    </>
  );
}
